/**
 * Seeded randomness and tileable noise, for painting surfaces.
 *
 * Its own generator, never the global one: the look of the building is the
 * same on every boot, and nothing drawn here can take a number from a stream
 * the simulation also draws from.
 */

/** A small fast seeded generator, returning [0, 1). */
export function rng(seed = 1) {
  let a = seed >>> 0 || 1;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A string to a seed, so a surface can be named rather than numbered. */
export function seedOf(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return h >>> 0;
}

/**
 * Value noise on a lattice that wraps every `period` cells, so a texture
 * painted with it tiles with no seam.
 */
export function periodicNoise(period, seed) {
  const next = rng(seed);
  const lattice = new Float32Array(period * period);
  for (let i = 0; i < lattice.length; i += 1) lattice[i] = next();
  const at = (x, y) => lattice[((y % period) + period) % period * period + (((x % period) + period) % period)];
  const smooth = (t) => t * t * (3 - 2 * t);
  return (x, y) => {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const fx = smooth(x - x0);
    const fy = smooth(y - y0);
    const a = at(x0, y0) + (at(x0 + 1, y0) - at(x0, y0)) * fx;
    const b = at(x0, y0 + 1) + (at(x0 + 1, y0 + 1) - at(x0, y0 + 1)) * fx;
    return a + (b - a) * fy;
  };
}

/**
 * Fractal noise over [0, 1) × [0, 1), tileable: `octaves` layers, each at twice
 * the frequency and `gain` of the amplitude of the last. Returns about [0, 1].
 */
export function fbm(seed, { base = 4, octaves = 4, gain = 0.5 } = {}) {
  const layers = [];
  for (let o = 0; o < octaves; o += 1) layers.push({ n: periodicNoise(base << o, seed + o * 1013), f: base << o, a: gain ** o });
  const total = layers.reduce((sum, l) => sum + l.a, 0);
  return (u, v) => {
    let sum = 0;
    for (const l of layers) sum += l.n(u * l.f, v * l.f) * l.a;
    return sum / total;
  };
}

export const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
export const mix = (a, b, t) => a + (b - a) * t;
/** 0xrrggbb to [r, g, b] in 0..255. */
export const rgb = (hex) => [(hex >> 16) & 255, (hex >> 8) & 255, hex & 255];
